import * as React from 'react';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Grid } from '@mui/material';
import InputMask from 'react-input-mask';
import { useState } from 'react';
import { getContacts, registerContact } from '../../../actions/contact';
import {
	capLetters,
	handleError,
	handleSuccess,
	handleWarning,
} from '../../../helpers/utils';
import { MultipleSelectChip } from '../../utils/UtilsComponent';
import { useEffect } from 'react';
import { updateOrder } from '../../../actions/orders';
import { getDataByUrl } from '../../../actions/catalogs';

export const ContactForm = ({
	open = null,
	handleClose = null,
	order = null,
	refreshOrder = null,
	newContact = null,
	setNewContact = null,
}) => {
	// Contact name
	const [contactName, setContactName] = useState('');
	// Phone
	const [phone, setPhone] = useState('');
	// Contacts from DB
	const [contactsDB, setContactsDB] = useState([]);
	// Selected contacts
	const [selectedContacts, setSelectedContacts] = useState([]);

	// Handle parse phone
	// XX-XX-XX-XX-XX --> XXXXXXXXXXXX
	const handleParsePhone = () => {
		const phoneArray = phone.split('');
		const nums = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
		const phoneFormatted = phoneArray.filter((c) => nums.includes(c)).join('');
		// Return phone with MX code
		return `52${phoneFormatted}`;
	};

	// Handle reset
	const handleResetForm = () => {
		setContactName('');
		setPhone('');
		setNewContact(false);
	};

	// Handle set contacts from db
	const handleGetContacts = () => {
		// Get contacts from DB
		getContacts().then(({ data }) => {
			setContactsDB(
				data.results.map((contact) => ({
					...contact,
					phone: contact.phone,
				}))
			);
		});
	};

	// Handle set local contacts
	const handleSetContacts = (currentOrder) => {
		if (order) {
			const currentContacts = currentOrder.contacts.map(
				(item) => item.name
			);
			setSelectedContacts(currentContacts);
		}
	};

	// Get contacts from DB once
	useEffect(() => {
		handleGetContacts();
	}, []);

	// Set current contacts when order changes
	useEffect(() => {
		handleSetContacts(order);
	}, [order]);

	// Update order contact list
	const handleUpdateOrderContactList = (phones) => {
		// Get unique values
		phones = phones.filter(
			(value, index, ref) => ref.indexOf(value) === index
		);

		// Update order
		updateOrder(order.id, {
			...order,
			client_id: order.client_id.id,
			created_by: order.created_by.id,
			contacts: phones,
		})
			.then(() => {
				// Feedback
				handleSuccess('Contactos actualizados');

				// Get (new) contacts
				handleGetContacts();

				// Refresh order
				refreshOrder();

				// Reset form
				handleResetForm();

				// CLose dialog
				handleClose();
			})
			.catch((error) => {
				// Feedback error
				handleError(
					error,
					'Ocurrió un error al intentar guardar la lista de contactos'
				);
			});
	};

	const handleSubscribe = (parsedPhone) => {
		getDataByUrl(`/api/order/contact/?phone=${parsedPhone}`)
			.then(({ data }) => {
				if (data.count > 0) {
					handleWarning('El número ya existe.');
				} else {
					// Register contact
					registerContact({
						name: capLetters(contactName),
						phone: parsedPhone,
					})
						.then(({ data }) => {
							// Refresh order
							handleUpdateOrderContactList([
								...order.contacts.map((item) => item.id),
								data.id,
							]);
			
							// Feedback
							handleSuccess(
								'Teléfono agregado a la lista de contactos subscritos a este pedido'
							);
			
							// Reset form
							handleResetForm();
			
							// Close dialog
							handleClose();
						})
						.catch((error) => {
							// Feedback errors
							handleError(
								error,
								'Ocurrió un problema al intentar registrar el contacto a la lista'
							);
						});
				}
			}).catch((error) => {
				// Feedback errors
				handleError(
					error,
					'Ocurrió un problema al intentar verificar si el teléfono ya existe.'
				);
			});
	};

	// Handle subscribe
	const handleSubmit = () => {
		// Setting a new contact
		if (newContact) {
			// Parse phone
			const parsedPhone = handleParsePhone();

			// Validate contact name
			if (contactName.length > 10) {
				// Validate phone
				if (parsedPhone.length === 12) {
					// Handle subscribe
					handleSubscribe(parsedPhone);
				} else {
					// Feedback warning
					handleWarning(
						'El teléfono debe de contener 10 dígitos.'
					);
				}
			} else {
				// Feedback warning
				handleWarning(
					'El nombre de contacto debe contener al menos 10 caracteres.'
				);
			}
		} else {
			// Set phones to an empty array
			let phones = [];

			// Get ids from contacts
			selectedContacts.forEach((nameItem) => {
				// Match local selected contacts with the db contacts
				const contact =
					contactsDB.find((contact) => contact.name === nameItem) ||
					null;
				// If contact is found then add it to contacts
				if (contact) phones.push(contact.id);
			});

			// Update contact list
			handleUpdateOrderContactList(phones);
		}
	};

	return (
		<div>
			<Dialog
				open={open}
				onClose={handleClose}
				maxWidth={newContact ? 'xs' : 'md'}
				fullWidth={!newContact}
				disableEscapeKeyDown
			>
				<DialogTitle>
					{newContact ? 'Nuevo contacto' : 'Contactos subscritos'}
				</DialogTitle>
				<DialogContent>
					{contactsDB?.length > 0 && (
						<Grid container spacing={1}>
							{newContact ? (
								<>
									<Grid item xs={12}>
										<TextField
											autoFocus
											margin='dense'
											id='name'
											label='Nombre'
											type='text'
											fullWidth
											variant='standard'
											helperText='Nombre completo del nuevo contacto'
											value={contactName}
											onChange={({ target }) => {
												setContactName(target.value);
											}}
										/>
									</Grid>
									<Grid item xs={12}>
										<InputMask
											placeholder='XX-XX-XX-XX-XX'
											mask='99-99-99-99-99'
											value={phone}
											disabled={false}
											maskChar=' '
											onChange={({ target }) => {
												setPhone(target.value);
											}}
										>
											{() => (
												<TextField
													fullWidth
													variant='standard'
													label='Teléfono'
													helperText='EJEMPLO: 22-26-75-48-35'
												/>
											)}
										</InputMask>
									</Grid>
								</>
							) : (
								<Grid item xs={12}>
									<MultipleSelectChip
										dataDB={
											contactsDB?.map(
												(contact) => contact.name
											) || []
										}
										personName={selectedContacts}
										setPersonName={setSelectedContacts}
									/>
								</Grid>
							)}
						</Grid>
					)}
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => setNewContact(!newContact)}
						color='primary'
					>
						{newContact ? 'Seleccionar' : 'Nuevo'}
					</Button>
					<Button
						onClick={() => {
							handleResetForm();
							handleClose();
						}}
						color='error'
					>
						Cancelar
					</Button>
					<Button
						onClick={handleSubmit}
						color='primary'
						disabled={
							newContact
								? contactName === '' && phone === ''
								: selectedContacts.length === 0
						}
					>
						{newContact ? 'Subscribir' : 'Guardar'}
					</Button>
				</DialogActions>
			</Dialog>
		</div>
	);
};
